import { KEEPALIVE_PING, KEEPALIVE_PONG, parseJsonMessage } from '../protocol';

export interface CobrowseSocketHandlers<IncomingMessage extends { type: string }> {
    onMessage: (message: IncomingMessage) => void;
    onBinary?: (frame: ArrayBuffer) => void;
    onOpen?: () => void;
    onPong?: () => void;
    onClose?: (code: number) => void;
}

export class CobrowseSocket<IncomingMessage extends { type: string }, OutgoingMessage extends { type: string }> {
    private readonly socket: WebSocket;

    constructor(url: string, handlers: CobrowseSocketHandlers<IncomingMessage>) {
        this.socket = new WebSocket(url);
        this.socket.binaryType = 'arraybuffer';
        this.socket.addEventListener('open', () => handlers.onOpen?.());
        this.socket.addEventListener('close', (event) => handlers.onClose?.(event.code));
        this.socket.addEventListener('message', (event: MessageEvent<string | ArrayBuffer>) => {
            if (typeof event.data !== 'string') {
                handlers.onBinary?.(event.data);
                return;
            }

            if (event.data === KEEPALIVE_PONG) {
                handlers.onPong?.();
                return;
            }

            const message = parseJsonMessage<IncomingMessage>(event.data);

            if (message) {
                handlers.onMessage(message);
            }
        });
    }

    get isOpen(): boolean {
        return this.socket.readyState === WebSocket.OPEN;
    }

    send(message: OutgoingMessage): void {
        if (this.isOpen) {
            this.socket.send(JSON.stringify(message));
        }
    }

    sendBinary(frame: Uint8Array): void {
        if (this.isOpen) {
            this.socket.send(frame);
        }
    }

    ping(): void {
        if (this.isOpen) {
            this.socket.send(KEEPALIVE_PING);
        }
    }

    close(): void {
        this.socket.close(1000);
    }
}

export function buildConnectUrl(relayUrl: string, sessionId: string, token: string): string {
    const url = new URL(`/sessions/${sessionId}/connect`, relayUrl);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.searchParams.set('token', token);

    return url.toString();
}
